/**
 * TierAssignmentPanel — Account Detail tier picker for PooPartner accounts.
 *
 * Renders nothing for Customer / Admin accounts. For PooPartners, shows the
 * current tier (badge chip + name + fee summary) and a dropdown to assign
 * a different tier from the full `partner_tiers` registry (server-sorted).
 *
 * IMPORTANT: As of Phase 3b, booking pricing reads commission from
 * `partner_tiers.commission_pct` via `_resolve_tier_commission_pct()` —
 * so changing a provider's tier DOES affect commission on *new* bookings.
 * `tier.fee_value` and the badge fields remain display/sort-only. The
 * legacy `provider_profile.commission_pct` field was removed entirely.
 *
 * Feb 2026 tier subscription batch additions:
 *   - Dropdown option format: `{name} — $X/mo · Y% commission`.
 *   - Deferred-billing caption below the dropdown.
 *   - Pending tier upgrade request banner (rendered when
 *     `detail.pending_tier_request` is non-null) with Approve / Deny
 *     buttons that open a confirm dialog with an optional admin_note.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Award, Loader2, Sparkles } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import BadgeChip from "@/components/BadgeChip";
import { api, formatApiErrorDetail } from "@/lib/api";


/**
 * Format a tier into the dropdown label: `Name — $X/mo · Y% commission`.
 * `monthly_price=0` still surfaces as `$0/mo` so admins can see the
 * difference between paid + free tiers at a glance.
 */
function formatTierOption(t) {
  const fee =
    t.fee_type === "flat"
      ? `$${Number(t.fee_value || 0).toFixed(2)} flat`
      : `${Number(t.fee_value || 0).toFixed(0)}% commission`;
  const price = `$${Number(t.monthly_price || 0).toFixed(0)}/mo`;
  return `${t.name} — ${price} · ${fee}`;
}


/**
 * Sub-component: pending tier upgrade request banner. Renders the partner's
 * pending request inline with two action buttons. Each opens a confirm
 * dialog where the admin can add an optional note (≤200 chars) before
 * actually resolving the request.
 */
function PendingTierRequestBanner({ detail, tiers, onResolved }) {
  const pending = detail?.pending_tier_request;
  const [action, setAction] = useState(null); // "approve" | "deny" | null
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  if (!pending) return null;

  // Joined tier docs for friendly names. Falls back to id if the tier is
  // missing (shouldn't happen — admin can't delete a tier with referencing
  // rows — but defensive).
  const tierById = (id) => tiers.find((t) => t.id === id);
  const reqTier = tierById(pending.requested_tier_id);
  const curTier = tierById(pending.current_tier_id);
  const reqLabel = reqTier?.name || pending.requested_tier_id;
  const curLabel = curTier?.name || pending.current_tier_id || "—";

  const closeDialog = () => {
    if (busy) return;
    setAction(null);
    setNote("");
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(
        `/admin/tier-upgrade-requests/${pending.id}/${action}`,
        { admin_note: note.trim() || null }
      );
      toast.success(action === "approve" ? "Approved — tier updated." : "Request denied.");
      // Refresh parent — easier than threading the new detail through.
      const { data } = await api.get(`/admin/accounts/${detail.id}`);
      onResolved?.(data);
      setAction(null);
      setNote("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div
        className="mb-4 rounded-2xl border-2 border-brand-orange/30 bg-orange-50/60 p-4 dark:bg-orange-950/30"
        data-testid="pending-tier-request-banner"
      >
        <div className="mb-2 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-brand-orange" aria-hidden="true" />
          <h5 className="font-heading text-sm font-semibold uppercase tracking-wider text-brand-orange">
            Pending tier upgrade request
          </h5>
        </div>
        <p className="mb-2 text-sm text-pp-text" data-testid="pending-tier-request-body">
          {detail?.name || detail?.email} requested to upgrade from{" "}
          <span className="font-semibold">{curLabel}</span> to{" "}
          <span className="font-semibold" data-testid="pending-tier-request-target">
            {reqLabel}
          </span>
          .
        </p>
        {pending.message && (
          <blockquote
            className="mb-3 border-l-4 border-brand-orange/40 pl-3 text-sm italic text-pp-text-muted"
            data-testid="pending-tier-request-message"
          >
            &ldquo;{pending.message}&rdquo;
          </blockquote>
        )}
        <p className="mb-3 text-xs text-pp-text-muted">
          Requested {String(pending.created_at).slice(0, 16).replace("T", " ")}.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={() => setAction("approve")}
            className="h-9 rounded-full bg-pp-orange px-4 text-sm text-pp-cta-text hover:bg-pp-orange-hover"
            data-testid="pending-tier-request-approve"
          >
            Approve
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setAction("deny")}
            className="h-9 rounded-full border border-pp-border px-4 text-sm hover:bg-brand-slate/10"
            data-testid="pending-tier-request-deny"
          >
            Deny
          </Button>
        </div>
      </div>

      <Dialog open={!!action} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="sm:max-w-md" data-testid="pending-tier-request-dialog">
          <DialogHeader>
            <DialogTitle>
              {action === "approve" ? `Approve upgrade to ${reqLabel}` : `Deny upgrade to ${reqLabel}`}
            </DialogTitle>
            <DialogDescription>
              {action === "approve"
                ? "The partner's tier will update immediately. Booking math is unaffected."
                : "The partner's tier stays unchanged."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
              Admin note (optional, ≤200 chars)
            </Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 200))}
              placeholder="Add a note for the audit trail…"
              data-testid="pending-tier-request-note"
              rows={3}
            />
            <p className="text-right text-xs text-pp-text-muted">{note.length}/200</p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={closeDialog}
              disabled={busy}
              data-testid="pending-tier-request-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submit}
              disabled={busy}
              className={
                action === "approve"
                  ? "bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
                  : "bg-brand-slate text-white hover:bg-brand-slate/90"
              }
              data-testid="pending-tier-request-confirm"
            >
              {busy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Working…
                </>
              ) : action === "approve" ? (
                "Confirm approval"
              ) : (
                "Confirm deny"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


export default function TierAssignmentPanel({ detail, onSaved }) {
  // Caller is expected to gate by account_type. Defensive null-check on
  // the data is handled inline below; the early-render `null` happens
  // AFTER all hooks so we obey React's rules-of-hooks.
  const isProvider = detail?.account_type === "PooPartner";
  const currentTier = detail?.tier; // joined tier doc from /admin/accounts/{id}
  const currentTierId = currentTier?.id || detail?.profile?.tier_id || null;

  const [tiers, setTiers] = useState([]);
  const [tiersLoading, setTiersLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(currentTierId || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isProvider) {
      setTiersLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/partner-tiers");
        if (!cancelled) setTiers(data || []);
      } catch (e) {
        if (!cancelled) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
      } finally {
        if (!cancelled) setTiersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isProvider]);

  useEffect(() => {
    setSelectedId(currentTierId || "");
  }, [currentTierId]);

  const dirty = useMemo(
    () => !!selectedId && selectedId !== currentTierId,
    [selectedId, currentTierId]
  );

  if (!isProvider) return null;

  const submit = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/admin/accounts/${detail.id}/tier`,
        { tier_id: selectedId }
      );
      toast.success("Tier updated.");
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-5" data-testid="tier-assignment-panel">
      <hr className="mb-5 border-pp-border" />
      <div className="mb-3 flex items-center gap-2">
        <Award className="h-5 w-5 text-brand-orange" aria-hidden="true" />
        <h4 className="font-heading text-lg font-semibold text-pp-text">
          Tier assignment
        </h4>
      </div>
      <p
        className="mb-4 text-xs text-pp-text-muted"
        data-testid="tier-assignment-subtitle"
      >
        Tier sets the partner's commission rate on new bookings (existing
        bookings keep their snapshot). It also drives badge + customer-facing
        sort order.
      </p>

      {/* Pending tier upgrade request banner (Feb 2026 tier subscription batch) */}
      <PendingTierRequestBanner
        detail={detail}
        tiers={tiers}
        onResolved={onSaved}
      />

      {/* Current tier readout */}
      <div className="mb-4 flex flex-wrap items-center gap-3" data-testid="tier-current-row">
        <span className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
          Current
        </span>
        {currentTier ? (
          <>
            {currentTier.badge_id && currentTier.badge_id !== "none" && (
              <BadgeChip
                badgeId={currentTier.badge_id}
                size="sm"
                testId="tier-current-badge"
              />
            )}
            <span
              className="font-semibold text-pp-text"
              data-testid="tier-current-name"
            >
              {currentTier.name}
            </span>
            <span
              className="text-xs text-pp-text-muted"
              data-testid="tier-current-price"
            >
              ${Number(currentTier.monthly_price || 0).toFixed(0)}/mo
            </span>
          </>
        ) : (
          <span className="text-pp-text-muted" data-testid="tier-current-none">
            — unassigned —
          </span>
        )}
      </div>

      {/* Change-tier dropdown + Save */}
      <div className="grid items-end gap-3 sm:grid-cols-[1fr_auto]">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
            Change tier
          </Label>
          <Select
            value={selectedId}
            onValueChange={setSelectedId}
            disabled={tiersLoading || saving}
          >
            <SelectTrigger
              className="mt-2 h-11 rounded-full"
              data-testid="tier-select-trigger"
            >
              <SelectValue placeholder={tiersLoading ? "Loading tiers…" : "Pick a tier"} />
            </SelectTrigger>
            <SelectContent>
              {tiers.map((t) => (
                <SelectItem
                  key={t.id}
                  value={t.id}
                  data-testid={`tier-select-option-${t.id}`}
                >
                  {formatTierOption(t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p
            className="mt-2 text-xs italic text-pp-text-muted"
            data-testid="tier-deferred-billing-caption"
          >
            Assigning a paid tier does NOT bill the partner yet (billing
            deferred to Stripe). For now, paid-tier assignments are
            effectively comps.
          </p>
        </div>
        <Button
          type="button"
          onClick={submit}
          disabled={!dirty || saving || tiersLoading}
          className="h-11 rounded-full bg-pp-orange px-6 text-pp-cta-text hover:bg-pp-orange-hover"
          data-testid="tier-save-button"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…
            </>
          ) : (
            "Save tier"
          )}
        </Button>
      </div>
    </section>
  );
}
